<template>
  <div>
    <!-- Overlay for mobile -->
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -- backdrop dismiss is a pointer convenience; the hamburger toggle is the keyboard path -->
    <div
      v-if="showSidebar && isMobile"
      class="sidebar-overlay"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <div
      class="sidebar"
      :class="{
        'sidebar-open': showSidebar,
        'sidebar-mobile': isMobile,
        'sidebar-desktop': !isMobile,
      }"
    >
      <!-- Logo -->
      <div class="sidebar-logo">
        <a href="https://console.isilive.ca" target="_blank">
          <img src="https://video.isilive.ca/cdn/isilive_logo.png" alt="" />
          <span>{{ $t("nav.adminConsole") }}</span>
        </a>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav" :aria-label="$t('a11y.mainNavigation')">
        <!-- Section 1: Core Management -->
        <div class="nav-section">
          <router-link to="/dashboard" class="nav-item" @click="handleNavClick">
            <v-icon>mdi-view-dashboard</v-icon>
            <span>{{
              isClientUser
                ? $t("clientPortal.dashboardTitle")
                : $t("routes.dashboard")
            }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/streams"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-video</v-icon>
            <span>{{ $t("nav.liveStreams") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/audience"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-account-eye</v-icon>
            <span>{{ $t("nav.audience") }}</span>
          </router-link>

          <!-- VOD Assets hidden for now — the VOD Browser (/files) is being
               tried as the primary VOD interface. Restore by uncommenting. -->
          <!--
          <router-link
            v-if="isAdmin"
            to="/videos"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-video-box</v-icon>
            <span>VOD Assets</span>
          </router-link>
          -->

          <router-link
            v-if="isAdmin || isClientUser"
            to="/files"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-folder-multiple</v-icon>
            <span>{{ $t("nav.vodBrowser") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin || isClientUser"
            to="/encoders"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-video-input-hdmi</v-icon>
            <span>{{ $t("nav.encoders") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/client-accounts"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-account-multiple</v-icon>
            <span>{{ $t("routes.clientAccounts") }}</span>
          </router-link>

          <!-- A client administrator manages their own organisation and its
               people. One entry per administered account, since a few people
               administer two. -->
          <router-link
            v-for="grant in clientAdminAccounts"
            :key="grant.client_account_uid"
            :to="`/client_account/${grant.client_account_uid}`"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-office-building-cog</v-icon>
            <span>{{ $t("clientPortal.myOrganization") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/users"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-account-group</v-icon>
            <span>{{ $t("routes.users") }}</span>
          </router-link>
        </div>

        <!-- Section 2: Tools -->
        <div class="nav-section">
          <router-link
            v-if="isAdmin"
            to="/shorturl"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-link-variant</v-icon>
            <span>{{ $t("routes.urlShortener") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/quickshare"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-share-variant</v-icon>
            <span>{{ $t("routes.quickShare") }}</span>
          </router-link>

          <!-- isi_video is a separate application on its own domain, so this
               is a plain external link, not a route. The host per environment
               comes from src/config.js like every other sibling service. -->
          <a
            v-if="isAdmin"
            :href="isiVideoAdminUrl"
            :title="$t('nav.isiVideoConsoleHint')"
            class="nav-item"
            target="_blank"
            rel="noopener"
            @click="handleNavClick"
          >
            <v-icon>mdi-television-play</v-icon>
            <span>{{ $t("nav.isiVideoConsole") }}</span>
            <v-icon class="nav-item-external">mdi-open-in-new</v-icon>
          </a>
        </div>

        <!-- Section 3: Administration -->
        <div class="nav-section">
          <router-link
            v-if="isAdmin"
            to="/encode_cluster"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-server</v-icon>
            <span>{{ $t("nav.encodingCluster") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/streaming_cluster"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-access-point-network</v-icon>
            <span>{{ $t("routes.inputCluster") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/api_cluster"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-server-network</v-icon>
            <span>{{ $t("routes.apiCluster") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/db_cluster"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-database</v-icon>
            <span>{{ $t("routes.dbCluster") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/deployments"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-rocket-launch</v-icon>
            <span>{{ $t("routes.deployments") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/release-notes"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-newspaper-variant-outline</v-icon>
            <span>{{ $t("routes.releaseNotes") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/audit-log"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-clipboard-text-clock</v-icon>
            <span>{{ $t("routes.auditLog") }}</span>
          </router-link>

          <router-link
            v-if="isAdmin"
            to="/platform-settings"
            class="nav-item"
            @click="handleNavClick"
          >
            <v-icon>mdi-alert-octagon</v-icon>
            <span>{{ $t("routes.platformSettings") }}</span>
          </router-link>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import { isClientUser, clientAdminGrants } from "../utils/clientAccess";
import { ISI_VIDEO_ADMIN_URL } from "@/config";

export default {
  name: "SimpleSidebar",
  data() {
    return {
      showSidebar: false,
      isMobile: false,
      isiVideoAdminUrl: ISI_VIDEO_ADMIN_URL,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.user_data.is_admin;
    },
    // Non-admin with at least one client_access grant.
    isClientUser() {
      return isClientUser(this.$store.state.user_data);
    },
    // Accounts this user administers (empty for staff, who reach every
    // account through Client Accounts instead).
    clientAdminAccounts() {
      return clientAdminGrants(this.$store.state.user_data).filter(
        (grant) => grant.client_account_uid,
      );
    },
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 960;
      // Set initial state: visible on desktop, hidden on mobile
      this.showSidebar = !this.isMobile;
    },
    toggleSidebar() {
      this.showSidebar = !this.showSidebar;
    },
    closeSidebar() {
      this.showSidebar = false;
    },
    handleNavClick() {
      // Close sidebar on mobile when nav item is clicked
      if (this.isMobile) {
        this.closeSidebar();
      }
    },
  },
};
</script>

<style scoped>
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
}

.sidebar {
  width: 260px;
  background: rgba(27, 27, 27, 0.87);
  color: white;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.sidebar-desktop {
  position: static;
  transform: none;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: 100%;
  flex-shrink: 0;
}

.sidebar-desktop:not(.sidebar-open) {
  width: 0;
  overflow: hidden;
}

.sidebar-mobile {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  height: 100vh;
  transform: translateX(-100%);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
}

.sidebar-mobile.sidebar-open {
  transform: translateX(0);
}

.sidebar-logo {
  padding: 20px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-logo a {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: white;
}

.sidebar-logo img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
}

.sidebar-logo span {
  font-size: 16px;
  font-weight: 400;
  text-transform: uppercase;
}

.sidebar-nav {
  padding: 20px 0;
  flex: 1;
}

.nav-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
}

.nav-section:not(:last-child) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  margin: 0 15px;
  border-radius: 4px;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.router-link-active {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.nav-item .v-icon {
  margin-right: 16px;
  font-size: 20px;
}

.nav-item span {
  font-size: 14px;
  font-weight: 300;
}

/* Trailing "opens in a new tab" marker on the external link. */
.nav-item .v-icon.nav-item-external {
  margin-left: auto;
  margin-right: 0;
  font-size: 16px;
  opacity: 0.6;
}

/* Adjust main content when sidebar is open on desktop */
.main-content-shifted {
  margin-left: 260px;
}

@media (max-width: 959px) {
  .sidebar {
    position: fixed;
  }
}
</style>
